import { useTranslation } from "react-i18next";

import { useEntitlementTier } from "@/lib/usePlanFacts";
import { primaryTenantApex } from "@/shell/tenantHosts";

const STEP_KEYS = ["pricing", "connect", "upload", "order"] as const;

export function PlatformSteps({ lang }: { lang: string }): JSX.Element {
  const { t } = useTranslation("common");
  // KAN-1944: step 02 promised "your domain" with no tier attached while the
  // pricing page said "from Pro". The shop address every tier gets is the
  // configured tenant apex; the custom domain names the tier that grants
  // ``custom_domain`` in the plan catalogue, or nothing until that is known.
  const apex = primaryTenantApex();
  const domainTier = useEntitlementTier("custom_domain", lang);
  const domainNote =
    domainTier?.kind === "all"
      ? t("platformLanding.steps.items.connect.domainAll")
      : domainTier?.kind === "from"
        ? t("platformLanding.steps.items.connect.domainFrom", {
            plan: domainTier.plan,
          })
        : null;

  // ``#jak`` is the in-page target the design's nav points at.
  return (
    <section className="section" id="jak">
      <div className="inner">
        <p className="kicker">{t("platformLanding.steps.kicker")}</p>
        <h2 className="h2" style={{ maxWidth: 780 }}>
          {t("platformLanding.steps.heading")}
        </h2>

        <ol className="steps" data-testid="platform-steps">
          {STEP_KEYS.map((key, i) => (
            <li key={key} className="step">
              <div className="step-head">
                <span className="step-num">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {i < STEP_KEYS.length - 1 && (
                  <svg
                    className="arr"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                )}
              </div>
              <h3>{t(`platformLanding.steps.items.${key}.title`)}</h3>
              <p>{t(`platformLanding.steps.items.${key}.body`, { apex })}</p>
              {key === "connect" && domainNote ? (
                <p data-testid="platform-steps-domain">{domainNote}</p>
              ) : null}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
